import { supportsPushNotifications } from "../../utils/browserCapabilities";
import { Card, H6 } from "@sellar-io/components";
import { useEffect } from "react";

import { PushNotificationSettingsDialog } from "./PushNotificationSettingsDialog";
import {
  PushNotificationEnableWalkthroughDialog,
  WalkthroughClient,
} from "./PushNotificationEnableWalkthroughDialog";
import { getDeviceId, usePushNotificationStore } from "./PushNotificationStore";
import { useNavigate } from "react-router";
import { useToast } from "../toast/useToast.shared";
import { useDashboardContext } from "../dashboard/Dashboard.shared";

import { getMessaging, onMessage, isSupported } from "firebase/messaging";
import { firebaseApp } from "../../firebaseApp";

export const PushNotifications = () => {
  // Call the zustand store as a hook — do not treat it as a plain value.
  const settingsActive = usePushNotificationStore(
    (state) => state.settingsActive,
  );
  const guideActive = usePushNotificationStore((state) => state.guideActive);
  const setSettingsActive = usePushNotificationStore(
    (state) => state.setSettingsActive,
  );
  const setGuideActive = usePushNotificationStore(
    (state) => state.setGuideActive,
  );

  const navigate = useNavigate();
  const { toast } = useToast();
  const { isTwoPanel } = useDashboardContext();

  let walkthroughClient: WalkthroughClient = "FALLBACK";
  const deviceId = getDeviceId().toLowerCase();
  if (deviceId.includes("chrome") && deviceId.includes("desktop"))
    walkthroughClient = "CHROME";
  if (deviceId.includes("safari") && deviceId.includes("desktop"))
    walkthroughClient = "SAFARI";
  if (deviceId.includes("firefox")) walkthroughClient = "FIREFOX";
  if (deviceId.includes("android")) walkthroughClient = "ANDROID";
  if (deviceId.includes("ios")) walkthroughClient = "IOS";

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    const setupMessaging = async () => {
      if (supportsPushNotifications() && (await isSupported()) && active) {
        const firebaseMessaging = getMessaging(firebaseApp);
        unsubscribe = onMessage(firebaseMessaging, (payload) => {
          const title = payload.data?.title;
          const body = payload.data?.body;
          const url = payload.data?.url;

          if (!title || !body) return;

          toast({
            placement: isTwoPanel ? "bottom" : "top",
            render: () => (
              <Card
                onClick={() => url && navigate(url)}
                className="mb-4 flex items-start border border-zinc-300 bg-white bg-zinc-100 p-4"
              >
                <img
                  src="https://storage.googleapis.com/sellar-partner-images/sellar-image-assets/sellar-icon.svg"
                  width={24}
                  height={24}
                />
                <div className="ml-4">
                  <H6 className="font-semibold text-zinc-800">{title}</H6>
                  <p>{body}</p>
                </div>
              </Card>
            ),
          });
        });
      }
    };

    void setupMessaging().catch(() => {
      // Push is optional, including when browser storage or worker access is blocked.
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [toast, isTwoPanel, navigate]);

  return (
    <>
      <PushNotificationSettingsDialog
        active={settingsActive}
        onDismiss={() => setSettingsActive(false)}
      />
      <PushNotificationEnableWalkthroughDialog
        active={guideActive}
        onDismiss={() => setGuideActive(false)}
        client={walkthroughClient}
      />
    </>
  );
};
